/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, map } from 'rxjs/operators';
import {
  InitializeHangerSpecifications,
  InitializeHangerSpecificationsSuccess,
  InitializeSystemInfo,
  InitializeSystemInfoSuccess,
  InitializeToolbeltAccordionGroups,
  InitializeToolbeltAccordionGroupsSuccess,
  UpdateSystemInfo,
  UpdateSystemInfoSuccess,
  sendPopupRoutingAction,
  sendPopupRoutingActionSuccess,
  InitializeRootPath,
  InitializeRootPathSuccess,
  initializeProjectListSuccess,
  initializeProjectList,
  getProcessFittingStatus,
  getProcessFittingStatusSuccess,
  updateIsClearance,
  updateIsClearanceSuccess,
  CheckInvalidShortcuts,
  CheckInvalidShortcutsSuccess,
  GetConfigurationLocation,
  GetConfigurationLocationSuccess,
  GetProjectsListSuccess,
  onAddProjectClickActionSuccess,
  onAddProjectClickAction,
  GetProjectsList,
  sendClosePopupAction,
  sendClosePopupActionSuccess,
} from './toolbelt.actions';

import { BackendService, DataInitializerService } from '../../../../utils/src';
import { BooleanObject } from '../models/boolean-object';
import { ElectricalSettingsReturnCodes } from '../models/electrical-settings-return-codes';
import { of } from 'rxjs';

@Injectable()
export class ToolbeltEffects {
  constructor(
    private readonly actions$: Actions,
    private dataInitializerService: DataInitializerService,
    private backendService: BackendService
  ) {}

  InitializeToolbeltAccordionGroups$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InitializeToolbeltAccordionGroups),
      concatMap(() =>
        this.dataInitializerService
          .InitializeToolbeltAccordionGroupsFromJson()
          .pipe(map((data) => InitializeToolbeltAccordionGroupsSuccess({ accordionGroups: data })))
      )
    );
  });

  InitializeSystemInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InitializeSystemInfo),
      concatMap(() =>
        this.dataInitializerService
          .InitializeSystemInfo()
          .pipe(map((data) => InitializeSystemInfoSuccess({ systemInfo: data })))
      )
    );
  });

  GetProjectsList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GetProjectsList),
      concatMap(() =>
        this.backendService.getProjectsList().pipe(map((data) => data && GetProjectsListSuccess({ projectList: data })))
      )
    );
  });

  UpdateSystemInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UpdateSystemInfo),
      concatMap(() =>
        this.dataInitializerService
          .updateSystemInfo()
          .pipe(map((data) => UpdateSystemInfoSuccess({ systemInfo: data })))
      )
    );
  });

  InitializeHangerSpecifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InitializeHangerSpecifications),
      concatMap(() =>
        this.dataInitializerService
          .InitializeHangerSpecificationFromJson()
          .pipe(map((data) => InitializeHangerSpecificationsSuccess({ hangerSpecifications: data })))
      )
    );
  });

  SendClosePopupAction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(sendClosePopupAction),
      concatMap(() => {
        this.backendService.sendClosePopupAction();
        return of(sendClosePopupActionSuccess({ response: 'OK' }));
      })
    );
  });

  SendPopupRoutingAction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(sendPopupRoutingAction),
      concatMap((action) => {
        this.backendService.sendPopupRouting(action.route, action.width, action.height);
        return of(sendPopupRoutingActionSuccess({ response: 'OK' }));
      })
    );
  });

  OnAddProjectClickAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onAddProjectClickAction),
      concatMap((action) => {
        this.backendService.onAddProjectClick(action.width, action.height);
        return of(onAddProjectClickActionSuccess());
      })
    )
  );

  RootPath$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InitializeRootPath),
      concatMap(() =>
        this.dataInitializerService
          .InitializeRootPath()
          .pipe(map((data) => InitializeRootPathSuccess({ filePath: data })))
      )
    );
  });

  ProjectListAction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(initializeProjectList),
      concatMap((action) =>
        this.dataInitializerService
          .InitializeProjectList()
          .pipe(map((data) => data && initializeProjectListSuccess({ projectList: data })))
      )
    );
  });

  ProcessFittingStatusAction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getProcessFittingStatus),
      concatMap(() =>
        this.dataInitializerService
          .getProcessStatus()
          .pipe(map((data: BooleanObject) => getProcessFittingStatusSuccess({ status: data || { value: false } })))
      )
    );
  });

  UpdateClearanceAction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateIsClearance),
      concatMap((action) =>
        this.dataInitializerService
          .updateClearance(action.systemName, action.isChecked)
          .pipe(map((data: BooleanObject) => data && updateIsClearanceSuccess({ isClearanceUpdated: data })))
      )
    );
  });

  CheckInvalidShortcut$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CheckInvalidShortcuts),
      concatMap(() =>
        this.backendService
          .checkInvalidShortcutsOnOpening()
          .pipe(map((data: ElectricalSettingsReturnCodes) => CheckInvalidShortcutsSuccess({ invalidShortcut: data })))
      )
    );
  });

  GetConfigurationLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GetConfigurationLocation),
      concatMap(() =>
        this.backendService
          .getConfigurationLocation()
          .pipe(map((data) => GetConfigurationLocationSuccess({ configurationLocation: data })))
      )
    );
  });
}
